import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { useConfigurationStore } from '@store/use-configuration-store';
import { useElearningWpRemoteStore } from '@store/use-ewr-store';
import { useFormStore } from '@store/use-form-store';
import { useUserStore } from '@store/use-user-store';

import { ROUTES } from '../constants/routes';
import type { ApiError } from '../types';
import { apis } from './api/Api';
import { useCreateVisitors } from './visitor-management';

export interface ElearningWpRemoteTestAnswer {
  answerToken: string;
  text: string;
}

export interface ElearningWpRemoteTestQuestion {
  id: number;
  idx: number;
  question: string;
  answers: ElearningWpRemoteTestAnswer[];
}

interface EvaluationItem {
  id: number;
  idx: number;
  correct: boolean;
}

export interface ElearningWpRemoteTestSubmitResponse {
  status: 'success' | 'fail';
  title?: string;
  message?: string;
  evaluation: Record<string, EvaluationItem> | EvaluationItem[];
}

function notifyError(err: ApiError) {
  notification.error({
    title: err?.response?.data?.errorMessage ?? 'Something went wrong',
    placement: 'topRight'
  });
}

// --- Register: raw API hooks ---

export const useElearningWpRemotePersonInfoQuery = (friendlyUrl: string) =>
  useQuery({
    queryKey: ['ewr-person-info', friendlyUrl],
    queryFn: () =>
      apis.elearningWpRemote.getPersonInfo(friendlyUrl).then((r) => r.data),
    enabled: !!friendlyUrl,
    refetchOnMount: 'always'
  });

export const useElearningWpRemoteUpdatePersonMutation = (
  friendlyUrl: string,
  contactId: string | null
) =>
  useMutation({
    mutationFn: (body: unknown) =>
      apis.elearningWpRemote.updatePerson(
        friendlyUrl,
        contactId as string,
        body
      ),
    onError: notifyError
  });

export const useElearningWpRemoteDataProtectionDeclineMutation = (
  friendlyUrl: string,
  contactId: string | null
) =>
  useMutation({
    mutationFn: () =>
      apis.elearningWpRemote.dataProtectionDecline(
        friendlyUrl,
        contactId as string,
        {
          decline: true
        }
      ),
    onError: notifyError
  });

// --- Video page actions ---

export const useElearningWpRemoteElearningAccept = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accepted: boolean) =>
      apis.elearningWpRemote.elearningAccept(
        friendly_url,
        ewrContactId as string,
        {
          elearningAccept: accepted
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['ewr-test', friendly_url, ewrContactId]
      });
    },
    onError: notifyError
  });
};

export const useElearningWpRemoteFinish = () => {
  const { friendly_url } = useConfigurationStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => apis.elearningWpRemote.finish(friendly_url),
    onSuccess: () => navigate(ROUTES.EWR_FINAL),
    onError: notifyError
  });
};

export const useElearningWpRemoteTestQuery = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  return useQuery<ElearningWpRemoteTestQuestion[]>({
    queryKey: ['ewr-test', friendly_url, ewrContactId],
    queryFn: () =>
      apis.elearningWpRemote
        .getElearningTest(friendly_url, ewrContactId as string)
        .then((r) => r.data.testQuestions as ElearningWpRemoteTestQuestion[]),
    enabled: !!ewrContactId
  });
};

interface SubmitTestBody {
  testAnswers: { questionId: number; answerToken: string }[];
}

export const useElearningWpRemoteSubmitTest = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      body: SubmitTestBody
    ): Promise<ElearningWpRemoteTestSubmitResponse> =>
      apis.elearningWpRemote
        .submitElearningTest(friendly_url, ewrContactId as string, body)
        .then((r) => r.data),
    onSuccess: async (result) => {
      if (result.status === 'fail') {
        await queryClient.invalidateQueries({
          queryKey: ['ewr-test', friendly_url, ewrContactId]
        });
      }
    },
    onError: notifyError
  });
};

interface VideoActions {
  onElearningComplete: (accepted: boolean) => Promise<void>;
  isLoading: boolean;
}

export const useDefaultVideoActions = (): VideoActions => {
  const { registeredPersons } = useFormStore();
  const { mutateAsync: createVisitors, isPending } = useCreateVisitors();
  const navigate = useNavigate();

  return {
    onElearningComplete: async (accepted: boolean) => {
      if (!accepted) {
        navigate(ROUTES.VR_WELCOME);
      } else if (accepted && registeredPersons.length) {
        await createVisitors([registeredPersons[0]]);
        navigate(ROUTES.VR_FINAL);
      }
    },
    isLoading: isPending
  };
};

// --- OTP ---

export const useElearningWpRemoteResendOtp = () => {
  const { friendly_url } = useConfigurationStore();
  return useMutation({
    mutationFn: () => apis.elearningWpRemote.generateOtp(friendly_url),
    onError: notifyError
  });
};

export const useElearningWpRemoteVerifyOtp = () => {
  const { friendly_url } = useConfigurationStore();
  const { accessToken, refreshToken, loginSuccess } = useUserStore();
  return useMutation({
    mutationFn: (otp: string) =>
      apis.elearningWpRemote.verifyOtp(friendly_url, { otp }),
    onSuccess: () => {
      loginSuccess({
        username: friendly_url,
        password: '',
        accessToken,
        refreshToken
      });
    },
    onError: notifyError
  });
};
